import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, TextField, Divider, Chip, List, ListItem, ListItemText, Autocomplete, Button, ButtonGroup, IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import Rating from '@mui/material/Rating';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import CalendarPanel from './CalendarPanel';
import EditBookingModal from './EditBookingModal';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../utils/api';

const ViewButton = styled(Button)(({ selected }) => ({
  textTransform: 'none',
  minWidth: '120px',
  backgroundColor: selected ? '#E4C984' : 'transparent',
  color: '#171717',
  '&:hover': { backgroundColor: selected ? '#cbb16f' : 'rgba(0,0,0,0.04)' },
  borderColor: '#E4C984',
  fontWeight: 700
}));

const GoldButtonGroup = styled(ButtonGroup)(() => ({
  '& .MuiButton-outlined': { borderColor: '#E4C984' },
  '& .MuiButton-outlined:hover': { borderColor: '#cbb16f', backgroundColor: 'rgba(228, 201, 132, 0.12)' },
  '& .MuiButtonBase-root.Mui-focusVisible': { boxShadow: '0 0 0 2px rgba(228, 201, 132, 0.6)' }
}));

const formatDateISO = (d) => d.toISOString().slice(0, 10);

const TherapistDesk = () => {
  const [activeView, setActiveView] = useState('desk');
  const [date, setDate] = useState(() => formatDateISO(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [therapistQuery, setTherapistQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingsByDate, setBookingsByDate] = useState({});
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: {}, equipment: {} });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showScheduleEditModal, setShowScheduleEditModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [local, setLocal] = useState({ status: '', billingSubmitted: false, therapistNote: '', therapistRating: null, redFlag: false, redFlagReasons: [] });
  const [dateRange, setDateRange] = useState([]);
  const [dayBookings, setDayBookings] = useState([]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiGet('/api/config');
        const data = await res.json();
        setConfig({
          rooms: data.rooms || {},
          treatments: data.treatments || [],
          therapists: data.therapists || {},
          equipment: data.equipment || {}
        });
      } catch {}
    };
    loadConfig();
  }, []);

  // Setup date range for timeline view
  useEffect(() => {
    const today = new Date();
    const range = [];
    for (let i = -7; i <= 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      range.push(d.toISOString().split('T')[0]);
    }
    setDateRange(range);
  }, []);

  // Fetch bookings for selected date (for timeline view)
  useEffect(() => {
    const loadBookings = async () => {
      try {
        const url = therapistQuery
          ? `/api/bookings/${date}?therapist=${encodeURIComponent(therapistQuery)}`
          : `/api/bookings/${date}`;
        const res = await apiGet(url);
        const data = await res.json();
        setDayBookings(Array.isArray(data) ? data : []);
      } catch {
        setDayBookings([]);
      }
    };
    if (date && activeView === 'timeline') loadBookings();
  }, [date, therapistQuery, activeView]);

  const monthDays = useMemo(() => {
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const end = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
    const startDay = new Date(start);
    startDay.setDate(start.getDate() - start.getDay());
    const endDay = new Date(end);
    endDay.setDate(end.getDate() + (6 - end.getDay()));
    const days = [];
    for (let d = new Date(startDay); d <= endDay; d.setDate(d.getDate() + 1)) {
      days.push(formatDateISO(new Date(d)));
    }
    return days;
  }, [monthAnchor]);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const promises = monthDays.map(async (d) => {
        const res = await apiGet(`/api/bookings/${d}`);
        if (!res.ok) return { date: d, bookings: [] };
        const data = await res.json();
        return { date: d, bookings: Array.isArray(data) ? data : [] };
      });
      const results = await Promise.all(promises);
      const map = {};
      results.forEach(({ date, bookings }) => { map[date] = bookings; });
      setBookingsByDate(map);
    } catch (e) {
      console.error('Failed to load bookings:', e);
    } finally {
      setLoading(false);
    }
  }, [monthDays]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const therapistList = useMemo(() => {
    return Object.keys(config.therapists || {}).map(name => ({
      name,
      ...config.therapists[name]
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [config.therapists]);

  const getStaffRoleLabel = useCallback((staffName) => {
    if (!staffName) return 'Therapist';
    const info = config.therapists?.[staffName];
    return info?.role || info?.title || 'Therapist';
  }, [config.therapists]);

  // Get unique roles from therapists
  const therapistRoles = useMemo(() => {
    const roles = new Set();
    Object.values(config.therapists || {}).forEach(t => {
      if (t.role) roles.add(t.role);
    });
    return Array.from(roles).sort();
  }, [config.therapists]);

  // Get booking counts per therapist for the selected date
  const therapistBookingCounts = useMemo(() => {
    const bookings = bookingsByDate[date] || [];
    const counts = {};
    bookings.forEach(b => {
      const therapist = b.therapist || b.therapistName;
      if (therapist) {
        counts[therapist] = (counts[therapist] || 0) + 1;
      }
    });
    return counts;
  }, [bookingsByDate, date]);

  // Filter therapists by role and show only those with bookings or all if no filter
  const filteredTherapists = useMemo(() => {
    return therapistList.filter(t => {
      if (roleFilter && t.role !== roleFilter) return false;
      // Only show therapists that have bookings on this date
      return therapistBookingCounts[t.name] > 0;
    });
  }, [therapistList, roleFilter, therapistBookingCounts]);

  const filterFn = useCallback((b) => {
    const tq = therapistQuery.trim().toLowerCase();
    if (tq) {
      // Check if therapist matches in the therapists array (multi-therapist support)
      const therapists = b.therapists || [];
      if (therapists.length > 0) {
        // Check if ANY of the assigned therapists matches the query
        const matches = therapists.some(t => String(t.name || '').toLowerCase().includes(tq));
        if (!matches) return false;
      } else {
        // Backward compatibility: check single therapist field
        const therapist = String(b.therapist || b.therapistName || '').toLowerCase();
        if (!therapist.includes(tq)) return false;
      }
    }
    return true;
  }, [therapistQuery]);

  const filteredByDate = useMemo(() => {
    const out = {};
    for (const d of monthDays) {
      const list = bookingsByDate[d] || [];
      out[d] = list.filter(filterFn);
    }
    return out;
  }, [bookingsByDate, monthDays, filterFn]);

  const totalMatches = useMemo(() => Object.values(filteredByDate).reduce((acc, arr) => acc + (arr?.length || 0), 0), [filteredByDate]);

  const goPrevMonth = () => {
    setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1));
  };
  const goNextMonth = () => {
    setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1));
  };
  const monthLabel = useMemo(() => monthAnchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), [monthAnchor]);

  const listHeader = useMemo(() => {
    if (therapistQuery) {
      const count = therapistBookingCounts[therapistQuery] || 0;
      return `${therapistQuery} (${count} booking${count !== 1 ? 's' : ''})`;
    }
    return 'All Bookings';
  }, [therapistQuery, therapistBookingCounts]);

  const bookingInstructions = (booking) => ({
    treatment: String(booking?.treatmentInstruction || booking?.treatment_instruction || '').trim(),
    general: String(booking?.generalInstruction || booking?.general_instruction || '').trim(),
  });

  const printDutyChart = () => {
    const rows = filteredByDate[date] || [];
    const escapeHtml = (value) => String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const title = therapistQuery ? `${therapistQuery} duty chart` : 'Therapist duty chart';
    const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    });
    const body = rows.map((b) => {
      const notes = bookingInstructions(b);
      return `<tr>
        <td>${escapeHtml(b.startTime)} – ${escapeHtml(b.endTime)}</td>
        <td>${escapeHtml(b.guestName || b.guest_name)}</td>
        <td>${escapeHtml(b.treatment)}</td>
        <td>${escapeHtml(b.room)}</td>
        <td>${escapeHtml(b.therapist)}</td>
        <td>${escapeHtml(notes.treatment) || '—'}</td>
        <td>${escapeHtml(notes.general) || '—'}</td>
      </tr>`;
    }).join('');
    const printWindowHtml = `<!DOCTYPE html><html><head><title>${escapeHtml(title)}</title>
      <style>
        @page { margin: 14mm; }
        body { font-family: Arial, sans-serif; color: #171717; margin: 0; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        p { margin: 0 0 16px; color: #555; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        thead { display: table-header-group; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: top; }
        tr, th, td { break-inside: avoid; page-break-inside: avoid; }
        th { background: #E4C984; }
      </style></head><body>
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(dateLabel)}</p>
      <table>
        <thead><tr><th>Time</th><th>Guest</th><th>Treatment</th><th>Room</th><th>Therapist</th><th>Treatment instruction</th><th>General instruction</th></tr></thead>
        <tbody>${body || '<tr><td colspan="7">No bookings</td></tr>'}</tbody>
      </table>
      </body></html>`;
    const blob = new Blob([printWindowHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const frame = document.createElement('iframe');
    frame.setAttribute('title', title);
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.src = url;
    document.body.appendChild(frame);
    frame.onload = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
        frame.remove();
      }, 1000);
    };
  };

  const stats = useMemo(() => {
    const total = dayBookings.length;
    const started = dayBookings.filter(b => String(b.status || '').toLowerCase() === 'started').length;
    const preClose = dayBookings.filter(b => String(b.status || '').toLowerCase() === 'pre_close').length;
    const closed = dayBookings.filter(b => String(b.status || '').toLowerCase() === 'closed').length;
    return { total, started, preClose, closed };
  }, [dayBookings]);

  const filteredConfig = useMemo(() => {
    // Start from base config
    const base = config || {};
    let therapistsObj = base.therapists || {};
    let roomsObj = base.rooms || {};
    let treatmentsArr = Array.isArray(base.treatments) ? base.treatments.slice() : [];

    // If therapist selected, keep only that therapist in therapist view
    if (therapistQuery) {
      therapistsObj = Object.fromEntries(Object.entries(therapistsObj).filter(([k]) => k === therapistQuery));

      // Build priority sets from current day bookings (already filtered to therapist)
      const roomSet = new Set(dayBookings.map(b => b.room).filter(Boolean));
      const treatmentSet = new Set(dayBookings.map(b => b.treatment).filter(Boolean));

      // Reorder rooms: selected therapist rooms first, then others, preserving original insertion order
      const roomEntries = Object.entries(roomsObj);
      const firstRooms = roomEntries.filter(([name]) => roomSet.has(name));
      const otherRooms = roomEntries.filter(([name]) => !roomSet.has(name));
      roomsObj = Object.fromEntries([...firstRooms, ...otherRooms]);

      // Reorder treatments similarly
      const firstTreatments = treatmentsArr.filter(t => treatmentSet.has(t.name));
      const otherTreatments = treatmentsArr.filter(t => !treatmentSet.has(t.name));
      treatmentsArr = [...firstTreatments, ...otherTreatments];
    }

    return { ...base, therapists: therapistsObj, rooms: roomsObj, treatments: treatmentsArr };
  }, [config, therapistQuery, dayBookings]);

  const statusOrder = ['scheduled','started','pre_close','closed'];

  const canEdit = useMemo(() => {
    if (!selectedBooking) return false;
    const today = new Date().toISOString().split('T')[0];
    if (selectedBooking.date !== today) return false;
    const now = new Date();
    const [h, m] = String(selectedBooking.startTime || '00:00').split(':').map(Number);
    const start = new Date();
    start.setHours(h || 0, m || 0, 0, 0);
    return now >= start;
  }, [selectedBooking]);

  const handleEditBooking = (booking, bookingDate) => {
    setSelectedBooking({ ...booking, date: bookingDate });
    setShowScheduleEditModal(true);
  };

  const handleOpenSession = (booking, bookingDate) => {
    setSelectedBooking({ ...booking, date: bookingDate });
    setLocal({
      status: String(booking.status || 'scheduled'),
      billingSubmitted: !!booking.billingSubmitted,
      therapistNote: booking.therapistNote || '',
      therapistRating: booking.therapistRating || null,
      redFlag: !!booking.redFlag,
      redFlagReasons: Array.isArray(booking.redFlagReasons) ? booking.redFlagReasons : [],
    });
    setShowSessionModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleEditModal(false);
    setSelectedBooking(null);
  };

  const handleCloseSessionModal = () => {
    setShowSessionModal(false);
    setSelectedBooking(null);
  };

  const handleSaveBooking = async () => {
    if (!selectedBooking) return;
    const payload = {
      status: local.status !== selectedBooking.status ? local.status : undefined,
      billingSubmitted: local.billingSubmitted !== selectedBooking.billingSubmitted ? local.billingSubmitted : undefined,
      therapistNote: local.therapistNote !== (selectedBooking.therapistNote || '') ? local.therapistNote : undefined,
      therapistRating: local.therapistRating !== (selectedBooking.therapistRating || null) ? local.therapistRating : undefined,
      redFlag: local.redFlag !== !!selectedBooking.redFlag ? local.redFlag : undefined,
      redFlagReasons: JSON.stringify(local.redFlagReasons) !== JSON.stringify(selectedBooking.redFlagReasons || []) ? local.redFlagReasons : undefined,
    };
    const clean = Object.fromEntries(Object.entries(payload).filter(([_,v]) => v !== undefined));
    if (Object.keys(clean).length === 0) { handleCloseSessionModal(); return; }
    
    try {
      const res = await apiPatch(`/api/bookings/${selectedBooking.bookingId || selectedBooking.id}/therapist`, clean);
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to update');
        return;
      }
      handleCloseSessionModal();
      fetchMonth(); // Refresh
    } catch (e) {
      alert('Failed to update booking');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f6f6f6' }}>
      {/* Top Header with View Toggle */}
      <Box sx={{ pt: 2, px: 2, pb: 0 }}>
        <GoldButtonGroup variant="outlined" size="small">
          <ViewButton selected={activeView === 'desk'} onClick={() => setActiveView('desk')} startIcon={<MedicalServicesOutlinedIcon fontSize="small" />}>Desk</ViewButton>
          <ViewButton selected={activeView === 'timeline'} onClick={() => setActiveView('timeline')} startIcon={<CalendarMonthOutlinedIcon fontSize="small" />}>Timeline</ViewButton>
        </GoldButtonGroup>
      </Box>

      {activeView === 'desk' && (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2, px: 2, pb: 2, pt: 2 }}>
          {/* Left: Therapist Workload Panel */}
          <Paper sx={{ width: 320, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Therapist Desk</Typography>

            {/* Current Date Display */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(228, 201, 132, 0.1)', borderRadius: 1, border: '1px solid #E4C984' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Current Date
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#171717' }}>
                {new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
            </Box>

            {/* Date Picker */}
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Select Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            {/* Role Filter */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Filter by Role</InputLabel>
              <Select
                value={roleFilter}
                label="Filter by Role"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {therapistRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* On-Duty Therapists List */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#171717' }}>
                On-Duty Therapists
              </Typography>
              <Chip 
                label={`${filteredTherapists.length} staff`} 
                size="small" 
                sx={{ bgcolor: 'rgba(228, 201, 132, 0.2)', color: '#171717' }}
              />
            </Box>

            <List dense>
              {filteredTherapists.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                  No therapists with bookings on this date
                </Typography>
              )}
              {filteredTherapists.map((therapist) => {
                const bookingCount = therapistBookingCounts[therapist.name] || 0;
                const isSelected = therapistQuery === therapist.name;
                return (
                  <ListItem
                    key={therapist.name}
                    button
                    selected={isSelected}
                    onClick={() => {
                      if (therapistQuery === therapist.name) {
                        setTherapistQuery('');
                      } else {
                        setTherapistQuery(therapist.name);
                      }
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: isSelected ? 'rgba(228, 201, 132, 0.15)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.1)' },
                      border: isSelected ? '1px solid #E4C984' : '1px solid transparent'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 500, flex: 1 }}>
                            {therapist.name}
                          </Typography>
                          <Chip 
                            label={bookingCount} 
                            size="small"
                            sx={{ 
                              minWidth: 32,
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: isSelected ? '#E4C984' : 'rgba(0,0,0,0.08)',
                              color: isSelected ? '#171717' : 'text.secondary'
                            }}
                          />
                        </Box>
                      }
                      secondary={therapist.role || therapist.title || 'Therapist'}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                  </ListItem>
                );
              })}
            </List>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Chip label="Loading..." size="small" />
              </Box>
            )}
          </Paper>

          {/* Middle: Results List */}
          <Paper sx={{ width: 420, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{listHeader}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<PrintOutlinedIcon />}
                onClick={printDutyChart}
                sx={{ color: '#171717', borderColor: '#E4C984' }}
                variant="outlined"
              >
                Print
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {(() => {
                const rows = filteredByDate[date] || [];
                if (rows.length === 0) {
                  return (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                      No bookings for this date
                    </Typography>
                  );
                }
                return rows.map((b, idx) => {
                  const notes = bookingInstructions(b);
                  const hasInstructions = Boolean(notes.treatment || notes.general);
                  return (
                  <ListItem 
                    key={b.bookingId || `${date}-${b.startTime}-${idx}`} 
                    sx={{ 
                      py: 0.75,
                      px: 1.5,
                      mb: 0.5,
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      '&:hover': { 
                        bgcolor: 'rgba(228, 201, 132, 0.08)',
                        borderColor: '#E4C984'
                      },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleEditBooking(b, date)}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSession(b, date);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            borderColor: '#E4C984',
                            color: '#171717',
                            '&:hover': { borderColor: '#cbb16f', bgcolor: 'rgba(228, 201, 132, 0.1)' },
                          }}
                        >
                          Session
                        </Button>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditBooking(b, date);
                          }}
                          sx={{
                            color: '#E4C984',
                            '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.2)' },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {b.startTime} - {b.endTime}
                            </Typography>
                            {b.redFlag && (
                              <FlagIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {b.treatment}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            Guest: {b.guestName}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            Room: {b.room}
                          </Typography>
                          {/* Multi-therapist display */}
                          {b.therapists && b.therapists.length > 1 ? (
                            <>
                              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 600 }}>
                                👥 Team: {b.therapists.map(t => t.name).join(', ')}
                              </Typography>
                              {(() => {
                                const currentTherapist = b.therapists.find(t => 
                                  String(t.name || '').toLowerCase().includes(therapistQuery.trim().toLowerCase())
                                );
                                return currentTherapist?.is_primary && (
                                  <Chip 
                                    label="PRIMARY" 
                                    size="small"
                                    color="primary"
                                    sx={{ 
                                      mt: 0.5,
                                      mr: 0.5,
                                      height: 18,
                                      fontSize: '0.6rem'
                                    }}
                                  />
                                );
                              })()}
                            </>
                          ) : (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              {getStaffRoleLabel(b.therapist)}: {b.therapist}
                            </Typography>
                          )}
                          {hasInstructions && (
                            <Chip
                              label="Instructions"
                              size="small"
                              title={[notes.treatment && `Treatment: ${notes.treatment}`, notes.general && `General: ${notes.general}`].filter(Boolean).join('\n')}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenSession(b, date);
                              }}
                              sx={{
                                mt: 0.5,
                                mr: 0.5,
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: '#E4C984',
                                color: '#171717',
                              }}
                            />
                          )}
                          {b.status && (
                            <Chip 
                              label={b.status} 
                              size="small" 
                              sx={{ 
                                mt: 0.5,
                                height: 18,
                                fontSize: '0.65rem',
                                textTransform: 'capitalize'
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  );
                });
              })()}
            </List>
          </Paper>

          {/* Right: Month Calendar */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ height: '100%', overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button size="small" onClick={goPrevMonth}>Prev</Button>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{monthLabel}</Typography>
                <Button size="small" onClick={goNextMonth}>Next</Button>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e5e7eb' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <Box key={day} sx={{ p: 1, textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {day}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {monthDays.map((d) => {
                  const isCurrentMonth = new Date(d).getMonth() === monthAnchor.getMonth();
                  const isToday = d === formatDateISO(new Date());
                  const isSelected = d === date;
                  const count = (filteredByDate[d] || []).length;
                  return (
                    <Box
                      key={d}
                      onClick={() => setDate(d)}
                      sx={{
                        p: 1,
                        minHeight: 80,
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(228, 201, 132, 0.1)' : isToday ? 'rgba(59, 130, 246, 0.05)' : '#fff',
                        opacity: isCurrentMonth ? 1 : 0.4,
                        '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.15)' }
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 400, color: isToday ? '#3b82f6' : 'inherit' }}>
                        {new Date(d + 'T00:00:00').getDate()}
                      </Typography>
                      {count > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                          {count} booking{count > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {activeView === 'timeline' && (
        <Box sx={{ flex: 1, minHeight: 0, px: 2, pb: 2, pt: 2 }}>
          <CalendarPanel
            selectedDate={date}
            onDateChange={setDate}
            dateRange={dateRange}
            config={filteredConfig}
            bookings={dayBookings}
            onEditBooking={(b) => handleEditBooking(b, date)}
            headerVariant="compact"
            showTitle={false}
            headerExtras={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Therapist</InputLabel>
                  <Select 
                    value={therapistQuery} 
                    label="Therapist" 
                    onChange={(e) => setTherapistQuery(e.target.value)}
                  >
                    <MenuItem value="">All Therapists</MenuItem>
                    {Object.keys(config.therapists || {}).map(name => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {therapistQuery && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip 
                      label={`${therapistQuery}`} 
                      size="small" 
                      sx={{ borderColor: '#E4C984', color: '#171717', backgroundColor: 'rgba(228, 201, 132, 0.2)' }} 
                      variant="outlined" 
                    />
                    <Chip label={`Sessions: ${stats.total}`} size="small" />
                    <Chip label={`Started: ${stats.started}`} size="small" />
                    <Chip label={`Pre-close: ${stats.preClose}`} size="small" />
                    <Chip label={`Closed: ${stats.closed}`} size="small" />
                  </Box>
                )}
              </Box>
            )}
          />
        </Box>
      )}

      {showScheduleEditModal && selectedBooking && (
        <EditBookingModal
          show={showScheduleEditModal}
          booking={selectedBooking}
          config={config}
          onClose={handleCloseScheduleModal}
          onDelete={async (b) => {
            const bookingId = b.id || b.bookingId;
            const bookingDate = b.date;
            if (!bookingId || !bookingDate) return;
            if (!window.confirm(`Delete appointment for ${b.guestName || b.guest_name}?`)) return;
            try {
              const resp = await apiDelete(`/api/bookings/${bookingDate}/${bookingId}`);
              if (resp.ok) {
                handleCloseScheduleModal();
                await fetchMonth();
              } else {
                const j = await resp.json().catch(() => ({}));
                alert(j.detail || 'Failed to delete');
              }
            } catch {
              alert('Error deleting appointment');
            }
          }}
          onSchedulingComplete={async () => {
            handleCloseScheduleModal();
            await fetchMonth();
          }}
        />
      )}

      {/* Therapist session updates */}
      <Dialog open={showSessionModal} onClose={handleCloseSessionModal} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Booking</DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.1fr 1fr 1fr' }, gap: 2 }}>
              {/* Left column: details */}
              <Box>
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Time</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.startTime} – {selectedBooking.endTime}</Typography>
                </Box>
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Guest</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.guestName}</Typography>
                </Box>
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Treatment</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.treatment}</Typography>
                </Box>
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Therapist</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.therapist}</Typography>
                </Box>
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Room</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.room}</Typography>
                </Box>
                {selectedBooking.assignedDoctor && (
                  <Box sx={{ mb: 1.25 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Doctor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedBooking.assignedDoctor}</Typography>
                  </Box>
                )}
              </Box>

              {/* Middle column: instructions */}
              <Box sx={{ borderLeft: { sm: '1px solid #eee' }, pl: { sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#171717' }}>Instructions</Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Treatment Instruction</Typography>
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 140, overflow: 'auto' }}>
                    <Typography variant="body2">{String(selectedBooking.treatmentInstruction || '').trim() || '—'}</Typography>
                  </Paper>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>General Instruction</Typography>
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 140, overflow: 'auto' }}>
                    <Typography variant="body2">{String(selectedBooking.generalInstruction || '').trim() || '—'}</Typography>
                  </Paper>
                </Box>
              </Box>

              {/* Right column: therapist updates */}
              <Box sx={{ borderLeft: { md: '1px solid #eee' }, pl: { md: 2 } }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#171717' }}>Therapist</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    {(() => {
                      const baseIdx = statusOrder.indexOf(String(selectedBooking.status || 'scheduled').toLowerCase());
                      const handleChange = (e) => {
                        const val = e.target.value;
                        if (statusOrder.indexOf(String(val)) < baseIdx) return;
                        setLocal(s => ({ ...s, status: val }));
                      };
                      return (
                        <Select value={local.status} label="Status" onChange={handleChange} disabled={!canEdit}>
                          <MenuItem value="scheduled" disabled={baseIdx > statusOrder.indexOf('scheduled')}>Scheduled</MenuItem>
                          <MenuItem value="started" disabled={baseIdx > statusOrder.indexOf('started')}>Started</MenuItem>
                          <MenuItem value="pre_close" disabled={baseIdx > statusOrder.indexOf('pre_close')}>Pre-close</MenuItem>
                          <MenuItem value="closed" disabled={baseIdx > statusOrder.indexOf('closed')}>Closed</MenuItem>
                        </Select>
                      );
                    })()}
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox
                    checked={!!local.billingSubmitted}
                    onChange={(e) => setLocal(s => ({ ...s, billingSubmitted: e.target.checked }))}
                    disabled={!canEdit || ['scheduled'].includes((local.status || 'scheduled').toLowerCase())}
                    sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' } }}
                  />
                  <Typography variant="body2">Billing submitted</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Rating
                    value={local.therapistRating || 0}
                    onChange={(e, v) => setLocal(s => ({ ...s, therapistRating: v }))}
                    readOnly={!canEdit || ['scheduled'].includes((local.status || 'scheduled').toLowerCase())}
                  />
                </Box>
                <TextField
                  label="Note"
                  value={local.therapistNote}
                  onChange={(e) => setLocal(s => ({ ...s, therapistNote: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={6}
                  disabled={!canEdit || ['scheduled'].includes((local.status || 'scheduled').toLowerCase())}
                />

                {/* Red Flag Section */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Checkbox
                      checked={!!local.redFlag}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setLocal(s => ({ 
                          ...s, 
                          redFlag: checked,
                          redFlagReasons: checked ? s.redFlagReasons : [] 
                        }));
                      }}
                      disabled={!canEdit || ['scheduled'].includes((local.status || 'scheduled').toLowerCase())}
                      sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: local.redFlag ? '#d32f2f' : 'text.primary' }}>
                      Red Flag
                    </Typography>
                  </Box>
                  
                  {local.redFlag && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                        Select reason(s):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {['Treatment Cust Short', 'Room issues', 'Time issues', 'Service issue', 'Communication issues'].map(reason => {
                          const isSelected = local.redFlagReasons.includes(reason);
                          return (
                            <Chip
                              key={reason}
                              label={reason}
                              size="small"
                              onClick={() => {
                                if (!canEdit || ['scheduled'].includes((local.status || 'scheduled').toLowerCase())) return;
                                setLocal(s => ({
                                  ...s,
                                  redFlagReasons: isSelected
                                    ? s.redFlagReasons.filter(r => r !== reason)
                                    : [...s.redFlagReasons, reason]
                                }));
                              }}
                              sx={{
                                bgcolor: isSelected ? '#d32f2f' : 'rgba(0,0,0,0.08)',
                                color: isSelected ? '#fff' : 'text.secondary',
                                '&:hover': {
                                  bgcolor: isSelected ? '#c62828' : 'rgba(0,0,0,0.12)',
                                },
                                cursor: canEdit && !['scheduled'].includes((local.status || 'scheduled').toLowerCase()) ? 'pointer' : 'default'
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSessionModal}>Cancel</Button>
          <Button
            onClick={handleSaveBooking}
            variant="contained"
            sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.9)' } }}
            disabled={!canEdit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TherapistDesk;